"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@shared/src/utils/cn";
import { Avatar, AvatarFallback, AvatarImage } from "@shared/src/components/ui/avatar";
import { IoChevronForward, IoSettingsSharp } from "react-icons/io5";
import { drawerNavigation, orderHistoryNavigation, mainLinks } from "@shared/src/config/navigation";
import { Button } from "@shared/src/components/ui/button";
import { useState } from "react";
import { UserAccountModal } from "@shared/src/components/common/dialogs/UserAccountModal";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { shortenAddress } from "@shared/src/utils/helpers";
import { NetworkAvatar } from "@shared/src/components/common/avatars/NetworkAvatar";

export function Sidebar() {
  const pathname = usePathname();
  const [isUserAccountOpen, setIsUserAccountOpen] = useState(false);
  const { activeAccount } = useAuthStore();

  return (
    <aside className="hidden md:flex flex-col h-[calc(100vh-130px)] w-20 lg:w-[280px] bg-surfaces-bg-primary border-r border-border-secondary fixed left-0">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="flex flex-col p-4">
          {/* Main Links */}
          <nav className="flex flex-col gap-1">
            {mainLinks.map(item => (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-xl text-text-tertiary hover:text-text-primary transition-colors",
                  pathname === item.href && "bg-surfaces-bg-secondary text-text-primary",
                )}
              >
                <item.icon size={24} />
                <span className="font-medium text-medium hidden lg:block">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Order History Section */}
          <div className="space-y-2 mt-8">
            <h3 className="text-small font-medium text-text-disabled hidden lg:block">Order History</h3>
            <nav className="flex flex-col gap-1">
              {orderHistoryNavigation.map(item => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-xl text-text-tertiary hover:text-text-primary transition-colors",
                    pathname === item.href && "bg-surfaces-bg-secondary text-text-primary",
                  )}
                >
                  <item.icon size={24} />
                  <span className="font-medium text-medium hidden lg:block">{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Account Section */}
          <div className="space-y-2 mt-8">
            <h3 className="text-small font-medium text-text-disabled hidden lg:block">Account</h3>
            <nav className="flex flex-col gap-1">
              {drawerNavigation.account.map(item => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-xl text-text-tertiary hover:text-text-primary transition-colors",
                    pathname === item.href && "bg-surfaces-bg-secondary text-text-primary",
                  )}
                >
                  <item.icon size={24} />
                  <span className="font-medium text-medium hidden lg:block">{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Section */}
      <div className="p-4">
        {/* Settings Link */}
        <Link
          href="/settings"
          className={cn(
            "flex items-center gap-3 p-3 rounded-xl text-text-tertiary hover:text-text-primary transition-colors",
            pathname === "/settings" && "bg-surfaces-bg-secondary text-text-primary",
          )}
        >
          <IoSettingsSharp size={24} />
          <span className="font-medium text-medium hidden lg:block">Settings</span>
        </Link>
      </div>

      {/* User Profile Section */}
      <div className="lg:px-6 max-lg:pl-3 my-2">
        <div
          className="flex items-center justify-between lg:px-4 lg:py-2.5 lg:bg-surfaces-bg-secondary lg:rounded-2xl cursor-pointer hover:opacity-75"
          onClick={() => setIsUserAccountOpen(true)}
        >
          <div className="flex items-center gap-4">
            <NetworkAvatar connectorIcon={activeAccount?.connectorIcon || ""} />
          </div>
          <div className="hidden lg:flex items-center gap-2">
            <Button variant="ghost-clean" size="icon" className="text-text-secondary">
              <IoChevronForward size={24} />
            </Button>
          </div>
        </div>
      </div>

      <UserAccountModal open={isUserAccountOpen} onOpenChange={setIsUserAccountOpen} />
    </aside>
  );
}
